<template>
  <div>
    <DrawerToolbar
      v-if="showToolbar"
      title="Content (Image/Text)"
      :admin="true"
      :showBackButton="true"
      :showCloseButton="false"
      @back="handleBack"
    />
    <v-card-text class="pb-0">
      <!-- Preview -->
      <Typography variant="subtitle-1" class="mb-2">Preview</Typography>
      <Content :config="Config" />
      <v-divider class="my-4" />
      <!-- Image section -->
      <v-switch
        v-model="imageEnabled"
        label="Image"
        color="brandPrimary"
        data-testid="image-toggle"
        hide-details
        class="mt-0 pt-0 mb-4"
      />
      <DragAndDropImageInput
        v-if="imageEnabled"
        :existingImageUrl="imageUrl"
        crop-image
        instructionText="The selection defaults to 4:1. Drag the handles to adjust."
        @file-selected="onFileSelected"
        @clear-existing-image="onClearImage"
        @crop-ratio-applied="onCropRatioApplied"
        data-testid="image-input"
      />

      <!-- Card Background -->
      <div class="my-4">
        <Typography variant="subtitle-1" class="mb-4">Card Background</Typography>
        <div class="d-flex align-center mb-2">
          <input
            type="color"
            v-model="primaryColor"
            aria-label="Background color"
            data-testid="bg-color-input"
            class="color-swatch mr-2"
          />
          <v-text-field
            v-model="primaryColor"
            dense
            outlined
            label="Primary color (in hexadecimal)"
            hide-details
          />
        </div>
        <div v-if="showGradient" class="d-flex align-center mb-2">
          <input
            type="color"
            v-model="secondaryColor"
            class="color-swatch mr-2"
            data-testid="secondary-color-input"
          />
          <v-text-field
            v-model="secondaryColor"
            dense
            outlined
            label="Secondary color (in hexadecimal)"
            hide-details
          />
          <v-btn
            icon
            class="ml-2"
            @click="handleRemoveGradient"
            aria-label="Remove gradient"
            data-testid="remove-gradient-btn"
          >
            <v-icon>mdi-delete</v-icon>
          </v-btn>
        </div>
        <Button
          v-if="!showGradient"
          text
          color="brandPrimary"
          class="pa-0"
          @click="handleAddGradient"
          data-testid="gradient-toggle-btn"
        >
          + Add Gradient
        </Button>
      </div>

      <v-divider class="my-4" />

      <!-- Title section -->
      <v-switch
        v-model="titleEnabled"
        label="Title"
        color="brandPrimary"
        data-testid="title-toggle"
        hide-details
        class="mt-0 pt-0 mb-4"
      />
      <div v-if="titleEnabled" data-testid="title-section">
        <v-text-field
          v-model="title"
          label="Title"
          placeholder="Add title (optional)"
          outlined
          dense
          color="brandPrimary"
          data-testid="title-input"
        />
        <div class="d-flex align-center mb-2" style="gap: 0">
          <input
            type="color"
            v-model="titleColor"
            aria-label="Title color"
            data-testid="title-color-picker"
            class="color-swatch mr-1"
          />
          <v-btn
            icon
            aria-label="Bold"
            @click="titleBold = !titleBold"
            :color="titleBold ? 'brandCyan' : ''"
            :class="{ 'format-btn--active': titleBold }"
            data-testid="title-bold-btn"
          >
            <v-icon>mdi-format-bold</v-icon>
          </v-btn>
          <v-btn
            icon
            aria-label="Italic"
            @click="titleItalic = !titleItalic"
            :color="titleItalic ? 'brandCyan' : ''"
            :class="{ 'format-btn--active': titleItalic }"
            data-testid="title-italic-btn"
          >
            <v-icon>mdi-format-italic</v-icon>
          </v-btn>
          <v-btn
            icon
            aria-label="Underline"
            @click="titleUnderline = !titleUnderline"
            :color="titleUnderline ? 'brandCyan' : ''"
            :class="{ 'format-btn--active': titleUnderline }"
            data-testid="title-underline-btn"
          >
            <v-icon>mdi-format-underline</v-icon>
          </v-btn>
          <v-btn
            icon
            aria-label="Align left"
            @click="titleAlignment = 'left'"
            :color="titleAlignment === 'left' ? 'brandCyan' : ''"
            :class="{ 'format-btn--active': titleAlignment === 'left' }"
            data-testid="title-align-left-btn"
          >
            <v-icon>mdi-format-align-left</v-icon>
          </v-btn>
          <v-btn
            icon
            aria-label="Align center"
            @click="titleAlignment = 'center'"
            :color="titleAlignment === 'center' ? 'brandCyan' : ''"
            :class="{ 'format-btn--active': titleAlignment === 'center' }"
            data-testid="title-align-center-btn"
          >
            <v-icon>mdi-format-align-center</v-icon>
          </v-btn>
          <v-btn
            icon
            aria-label="Align right"
            @click="titleAlignment = 'right'"
            :color="titleAlignment === 'right' ? 'brandCyan' : ''"
            :class="{ 'format-btn--active': titleAlignment === 'right' }"
            data-testid="title-align-right-btn"
          >
            <v-icon>mdi-format-align-right</v-icon>
          </v-btn>
          <v-select
            v-model="titleSize"
            :items="titleSizeItems"
            item-text="text"
            item-value="value"
            label="Size"
            outlined
            dense
            hide-details
            style="flex: 1 0 0"
            data-testid="title-size-select"
          />
        </div>
      </div>

      <v-divider class="my-4" />

      <!-- Body section -->
      <v-switch
        v-model="bodyEnabled"
        label="Body"
        color="brandPrimary"
        data-testid="body-toggle"
        hide-details
        class="mt-0 pt-0 mb-4"
      />
      <div v-if="bodyEnabled" data-testid="body-section">
        <ContentRichTextEditor v-model="body" placeholder="Add body text (optional)" class="mb-2" />
        <div class="d-flex align-center mb-2" style="gap: 0">
          <input
            type="color"
            v-model="bodyColor"
            aria-label="Body color"
            data-testid="body-color-picker"
            class="color-swatch mr-1"
          />
          <v-btn
            icon
            aria-label="Align left"
            @click="bodyAlignment = 'left'"
            :color="bodyAlignment === 'left' ? 'brandCyan' : ''"
            :class="{ 'format-btn--active': bodyAlignment === 'left' }"
            data-testid="body-align-left-btn"
          >
            <v-icon>mdi-format-align-left</v-icon>
          </v-btn>
          <v-btn
            icon
            aria-label="Align center"
            @click="bodyAlignment = 'center'"
            :color="bodyAlignment === 'center' ? 'brandCyan' : ''"
            :class="{ 'format-btn--active': bodyAlignment === 'center' }"
            data-testid="body-align-center-btn"
          >
            <v-icon>mdi-format-align-center</v-icon>
          </v-btn>
          <v-btn
            icon
            aria-label="Align right"
            @click="bodyAlignment = 'right'"
            :color="bodyAlignment === 'right' ? 'brandCyan' : ''"
            :class="{ 'format-btn--active': bodyAlignment === 'right' }"
            data-testid="body-align-right-btn"
          >
            <v-icon>mdi-format-align-right</v-icon>
          </v-btn>
          <v-select
            v-model="bodySize"
            :items="bodySizeItems"
            item-text="text"
            item-value="value"
            label="Size"
            outlined
            dense
            hide-details
            style="flex: 1 0 0"
            data-testid="body-size-select"
          />
        </div>
      </div>

      <!-- Button section -->
      <v-divider class="my-4" />
      <div class="d-flex align-center">
        <v-switch
          v-model="buttonEnabled"
          color="brandCyan"
          hide-details
          class="mt-0 mr-2"
          data-testid="button-toggle"
        />
        <span class="section-title">Button</span>
      </div>
      <div v-if="buttonEnabled" data-testid="button-section" class="mt-4">
        <v-text-field
          v-model="buttonText"
          label="Button Label"
          outlined
          dense
          color="brandCyan"
          :rules="buttonTextRules"
          :error-messages="buttonTextError"
          data-testid="button-text-input"
        />
        <v-text-field
          v-model="buttonLink"
          label="Button Link"
          outlined
          dense
          color="brandCyan"
          :rules="buttonLinkRules"
          :error-messages="buttonLinkError"
          data-testid="button-link-input"
        />
        <div class="d-flex align-center">
          <span class="mr-2">Button Color</span>
          <input
            type="color"
            v-model="buttonColor"
            class="color-swatch-sm mr-4"
            data-testid="button-color-picker"
          />
          <span class="mr-2">Text Color</span>
          <input
            type="color"
            v-model="buttonTextColor"
            class="color-swatch-sm"
            data-testid="button-text-color-picker"
          />
        </div>
      </div>
    </v-card-text>

    <!-- Footer -->
    <p
      v-if="saveError"
      role="alert"
      data-testid="save-error"
      class="error--text caption px-4 pb-2 text-center"
    >
      {{ saveError }}
    </p>
    <div class="d-flex justify-end px-4 pt-6 pb-4">
      <Button
        :dataTestId="'cancel-btn'"
        text
        color="brandPrimary"
        class="mr-4"
        @click="handleCancel"
        >Cancel</Button
      >
      <Button
        :dataTestId="'save-btn'"
        color="brandPrimary"
        :loading="saving"
        :disabled="saving"
        @click="onSave"
        >Save</Button
      >
    </div>

    <!-- Unsaved changes modal -->
    <UnsavedChangesDialog v-model="showUnsavedModal" @stay="handleStay" @discard="handleDiscard" />
  </div>
</template>

<script>
import ProgramService from "@/services/ProgramService";
import Content from "@/components/program/participant/dashboard/Content.vue";
import DrawerToolbar from "@/components/generic/DrawerToolbar.vue";
import UnsavedChangesDialog from "@/components/generic/UnsavedChangesDialog.vue";
import DragAndDropImageInput from "@/components/DragAndDropImageInput.vue";
import Button from "@/components/generic/Button.vue";
import Typography from "@/components/generic/Typography.vue";
import ContentRichTextEditor from "@/components/account-management/dashboard/ContentRichTextEditor.vue";
import { isValidImageUrl } from "@/utils/isValidImageUrl";
import { mapState } from "vuex";

function initialState() {
  return {
    isDirty: false,
    showUnsavedModal: false,

    // Image
    imageEnabled: true,
    imageUrl: null,
    imageFile: null,
    imageAspectRatio: false,

    // Background
    // ADR-0001 Part B: style/color leaves are backed by an underscored data
    // field plus an explicit `<field>Touched` flag. The public computed
    // getter/setter pair (see `computed`) is the only path that flips the
    // touched flag to true — hydration from server config / globalDefaults
    // writes the backing field directly (bypassing the setter) so preload
    // never counts as a user edit, even when the preloaded value happens to
    // equal the field's hardcoded default.
    primaryColorRaw: "#FFFFFF",
    primaryColorTouched: false,
    showGradient: false,
    secondaryColorRaw: "#FFFFFF",
    secondaryColorTouched: false,

    // Title
    titleEnabled: true,
    title: null,
    titleColorRaw: "#000000",
    titleColorTouched: false,
    titleSize: "md",
    titleBold: false,
    titleItalic: false,
    titleUnderline: false,
    titleAlignment: "center",
    bodyAlignment: "center",

    // Body
    // Bug 8 Quill migration: body is now real HTML managed by
    // ContentRichTextEditor's Quill instance -- there is no separate
    // bodyBold/bodyItalic/bodyUnderline reactive state anymore (bold/italic/
    // underline formatting lives inline in the HTML itself, same as
    // ContentRichTextEditor's own contract). `contentFormat` distinguishes
    // legacy V1 rows (plain text + bool flags, needing auto-convert-wrap on
    // preload) from V2 rows (raw HTML, stored/loaded as-is). Defaults to
    // "v1" so pre-migration configs (missing the key entirely) are treated
    // as legacy on preload -- see preloadEditor().
    contentFormat: "v1",
    bodyEnabled: true,
    body: null,
    bodyColorRaw: "#000000",
    bodyColorTouched: false,
    bodySize: "md",

    // Button section
    buttonEnabled: false,
    buttonText: null,
    buttonLink: null,
    buttonColorRaw: "#09ACEC",
    buttonColorTouched: false,
    buttonTextColorRaw: "#FFFFFF",
    buttonTextColorTouched: false,

    // Validation
    buttonTextError: null,
    buttonLinkError: null,

    // API / form state
    rowId: null,
    saving: false,
    saveError: null,
  };
}

// Lightweight, deliberately conservative detector for "this text already
// contains real HTML markup" -- used by preloadEditor() to decide whether a
// legacy (non-"v2") row's body text was actually authored as real HTML
// directly in the old plain-textarea editor (e.g. the OneTeam client -- see
// preloadEditor()'s contentFormat branch below). Confirmed (2026-07-20) that
// this is standard, sanctioned V1 authoring behavior, not content that
// bypassed the editor: the old textarea + v-html preview/render pipeline
// always supported real HTML typed directly into the Body field. Rows that
// match this detector must be routed through the SAME raw, unescaped preload
// path as genuine V2 content, never through wrapLegacyBodyFormatting()'s
// escapeHtml() step -- doing so would silently convert real, working markup
// into dead, escaped text the moment the row is next saved.
//
// Bare `<br>`/`<br />` tags are stripped before testing (contrarian review
// finding, 2026-07-20, BLOCKING): the historical V1 encode pipeline
// (FormattedBody(), see decodeHtmlBody() below) converted every newline into
// a literal `<br />`, so any multi-line legacy body -- an extremely common
// case, not an edge case -- already contains what looks like a tag. Without
// this exclusion, ordinary multi-paragraph V1 rows with bold/italic/
// underline flags set were silently routed into the raw path, permanently
// dropping their legacy formatting on first save even though they never
// contained any real pre-existing markup. A false positive on genuine plain
// text (e.g. literal bracket placeholder text like "<TBD>") remains safe
// here since plain text has no tags to strip in the first place; a false
// negative would reintroduce the destructive escape path, so the pattern is
// intentionally broad (any real opening tag with a recognizable tag name)
// beyond bare line breaks.
function looksLikeStructuredHtml(text) {
  if (!text) return false;
  const withoutLineBreaks = text.replace(/<br\s*\/?>/gi, "");
  return /<([a-z][a-z0-9]*)\b[^>]*>/i.test(withoutLineBreaks);
}

// ADR-0001 Part B: deep, per-field null-coalesce merge used when persisting
// an edit to an existing column. `buildConfig()` intentionally nulls out
// untouched style leaves so the backend's read-time merge can take over —
// but the save payload itself still needs to preserve any concrete value
// the row already has for a field the user didn't touch *this session*,
// since this frontend change (alone) does not rewrite already-stored rows.
// Null in `newConfig` -> fall back to `existingConfig`'s value for that leaf.
// Non-null in `newConfig` -> always wins (explicit user edit).
function nullCoalesceMergeConfig(existingConfig, newConfig) {
  const existing = existingConfig || {};
  const merged = { ...existing };
  for (const [key, value] of Object.entries(newConfig || {})) {
    if (value && typeof value === "object" && !Array.isArray(value)) {
      const existingNested =
        existing[key] && typeof existing[key] === "object" ? existing[key] : {};
      const mergedNested = { ...existingNested };
      for (const [nestedKey, nestedValue] of Object.entries(value)) {
        mergedNested[nestedKey] =
          nestedValue != null ? nestedValue : existingNested[nestedKey] ?? null;
      }
      merged[key] = mergedNested;
    } else {
      merged[key] = value != null ? value : existing[key] ?? null;
    }
  }
  return merged;
}

// Pre-Bug-8 (V1/legacy) records saved plain text HTML-entity-encoded
// (config.body.text = FormattedBody) plus separate body.bold/italic/
// underline booleans, so that plain text typed into the old textarea
// rendered safely via `v-html` on the participant side. preloadEditor()
// must therefore decode config.body.text back to raw text when loading an
// existing V1 widget for editing -- this is the exact inverse of the old
// encode transform, in reverse order (undo the last transform first):
// `<br />` -> newline, then unescape entities with `&amp;` LAST so an
// original literal "&lt;" (encoded as "&amp;lt;") isn't mis-decoded to "<"
// by decoding &amp; before &lt;. V2 (Quill/rich-HTML) records are exempt --
// they store real HTML directly and must never be run through this legacy
// decode (see preloadEditor()'s contentFormat branch).
function decodeHtmlBody(text) {
  if (!text) return text;
  return text
    .replace(/<br \/>/g, "\n")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&quot;/g, '"')
    .replace(/&#039;/g, "'")
    .replace(/&amp;/g, "&");
}

// Contrarian review finding (2026-07-15), BLOCKING: decodeHtmlBody() above
// correctly recovers the admin's original literal plain text, but that text
// is about to be assigned into an HTML context -- Quill's `innerHTML`
// (ContentRichTextEditor.vue) at preload time, and later `v-html`
// (Content.vue) once a re-save upgrades the record to contentFormat: "v2".
// V1 admins typed into a plain textarea with no rich-text capability, so any
// HTML-significant character they typed (e.g. "<SAVE20>", "Spend > $50 &
// save", or worse) was never meant to become live markup. escapeHtml()
// re-encodes those characters immediately before wrapLegacyBodyFormatting()
// adds the bold/italic/underline tags, so the ONLY live markup the V1
// back-compat path can ever introduce is the three formatting tags this
// function itself controls -- never anything from the admin's original
// text. Order matters: `&` MUST be escaped first, or the `&` produced by
// escaping `<`/`>`/quotes would itself be re-escaped into `&amp;amp;`.
function escapeHtml(text) {
  if (!text) return text;
  return text
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");
}

// Bug 8 Quill migration: legacy V1 records stored body text as a plain
// string plus separate body.bold/italic/underline booleans (rendered via
// additive CSS classes on the participant side, see Content.vue). To
// preserve backwards compatibility once the editor is upgraded to Quill's
// real HTML, V1 records are converted ONCE on preload into equivalent
// inline HTML by escaping the decoded text (see escapeHtml() above -- the
// text is display content, never markup) and wrapping the result in the
// corresponding tags. Canonical nesting order (innermost-to-outermost):
// <strong> (bold), then <em> (italic), then <u> (underline) -- i.e.
// bold+italic+underline nests as <u><em><strong>{text}</strong></em></u>.
// This runs only during preloadEditor() for V1/legacy configs; V2 configs
// already contain real HTML and skip this step entirely.
function wrapLegacyBodyFormatting(text, { bold, italic, underline } = {}) {
  if (!text) return text;
  let wrapped = escapeHtml(text);
  if (bold) wrapped = `<strong>${wrapped}</strong>`;
  if (italic) wrapped = `<em>${wrapped}</em>`;
  if (underline) wrapped = `<u>${wrapped}</u>`;
  return wrapped;
}

export default {
  name: "ContentCreator",
  components: {
    DrawerToolbar,
    UnsavedChangesDialog,
    DragAndDropImageInput,
    Content,
    Button,
    Typography,
    ContentRichTextEditor,
  },
  props: {
    isMobile: { type: Boolean, default: false },
    newRowId: { type: Number, required: false },
    row: { type: Object, required: false },
    column: { type: Object, required: false },
    rowCount: { type: Number, required: false },
    widgetTypes: { type: Array, required: false },
    clientId: { type: [Number, String], default: null },
    showToolbar: { type: Boolean, default: true },
    position: { type: Number, default: 1 },
    globalDefaults: { type: Object, default: () => ({}) },
  },
  data() {
    return initialState();
  },
  computed: {
    ...mapState(["globalClientId"]),
    titleSizeItems() {
      return [
        { text: "Small", value: "sm" },
        { text: "Medium", value: "md" },
        { text: "Large", value: "lg" },
        { text: "Extra Large", value: "xl" },
      ];
    },
    bodySizeItems() {
      return [
        { text: "Small", value: "sm" },
        { text: "Medium", value: "md" },
        { text: "Large", value: "lg" },
        { text: "Extra Large", value: "xl" },
      ];
    },
    buttonTextRules() {
      if (!this.buttonEnabled) return [];
      return [(v) => !!v || "Button Label is required"];
    },
    buttonLinkRules() {
      if (!this.buttonEnabled) return [];
      return [(v) => !!v || "Button Link is required"];
    },
    // ADR-0001 Part B — computed get/set pairs for touched-tracked style
    // leaves. The setter is the ONLY path that flips `<field>Touched`; it
    // runs unconditionally on assignment (even if the new value equals the
    // current one), unlike a `preloaded !== current` comparison which can't
    // distinguish "explicitly set to the same value" from "never touched".
    // Hydration code paths (preloadEditor) write the backing `_field`
    // directly, bypassing this setter, so preload never counts as touched.
    primaryColor: {
      get() {
        return this.primaryColorRaw;
      },
      set(val) {
        this.primaryColorRaw = val;
        this.primaryColorTouched = true;
      },
    },
    secondaryColor: {
      get() {
        return this.secondaryColorRaw;
      },
      set(val) {
        this.secondaryColorRaw = val;
        this.secondaryColorTouched = true;
      },
    },
    titleColor: {
      get() {
        return this.titleColorRaw;
      },
      set(val) {
        this.titleColorRaw = val;
        this.titleColorTouched = true;
      },
    },
    bodyColor: {
      get() {
        return this.bodyColorRaw;
      },
      set(val) {
        this.bodyColorRaw = val;
        this.bodyColorTouched = true;
      },
    },
    buttonColor: {
      get() {
        return this.buttonColorRaw;
      },
      set(val) {
        this.buttonColorRaw = val;
        this.buttonColorTouched = true;
      },
    },
    buttonTextColor: {
      get() {
        return this.buttonTextColorRaw;
      },
      set(val) {
        this.buttonTextColorRaw = val;
        this.buttonTextColorTouched = true;
      },
    },
    Config() {
      const config = {};

      config.contentFormat = "v2";

      config.image = { enabled: this.imageEnabled, aspectRatio: this.imageAspectRatio || false };
      if (this.imageEnabled && this.imageUrl) {
        config.image.url = this.imageUrl;
      }

      if (this.showGradient && this.secondaryColor) {
        config.background = {
          gradient: `linear-gradient(${this.primaryColor}, ${this.secondaryColor})`,
        };
      } else if (this.primaryColor) {
        config.background = { color: this.primaryColor };
      }

      config.title = {
        enabled: this.titleEnabled,
        text: this.title || "",
        color: this.titleColor,
        bold: this.titleBold,
        italic: this.titleItalic,
        underline: this.titleUnderline,
        size: this.titleSize,
        align: this.titleAlignment,
      };

      config.body = {
        enabled: this.bodyEnabled,
        // Bug 8 Quill migration: `this.body` is now real HTML produced by
        // ContentRichTextEditor's Quill instance, rendered as-is via
        // `v-html` on the participant side (see Content.vue) -- no more
        // HTML-entity-encoding (FormattedBody) needed or wanted here, since
        // that would show the live preview literal escaped tags instead of
        // rendered bold/italic/underline. bold/italic/underline are
        // hardcoded false because formatting now lives inline in the HTML
        // itself, same as buildConfig()'s save-time shape.
        text: this.body || "",
        color: this.bodyColor,
        bold: false,
        italic: false,
        underline: false,
        size: this.bodySize,
        align: this.bodyAlignment,
      };

      if (this.buttonEnabled) {
        if (this.buttonText) {
          config.button = {
            text: this.buttonText,
            color: this.buttonColor,
            textColor: this.buttonTextColor,
          };
        }
        if (this.buttonLink) {
          config.externalUrl = this.buttonLink;
        }
      }

      return config;
    },
  },
  created() {
    this._preloading = true;
    this._createdColumnId = null;
    this.preloadEditor();
    this.$nextTick(() => {
      this._preloading = false;
      this.isDirty = false;
    });
  },
  methods: {
    markDirty() {
      if (!this._preloading) this.isDirty = true;
    },
    resetPage() {
      Object.assign(this.$data, initialState());
    },
    preloadEditor() {
      this.resetPage();
      const editing = !!(this.column?.columnId ?? this.column?.dashboardColumnId);

      if (editing) {
        const config = this.column.config;
        // Bug 8 Quill migration: pre-migration configs predate the
        // `contentFormat` field entirely, so their absence must be treated
        // as legacy V1 (not V2) -- default to "v1" when the key is missing.
        this.contentFormat = config?.contentFormat || "v1";

        if (config?.image) {
          this.imageEnabled = config.image.enabled !== undefined ? config.image.enabled : true;
          this.imageUrl = config.image.url || null;
          this.imageAspectRatio = !!config.image.aspectRatio;
        }

        if (config?.background?.gradient) {
          const match = config.background.gradient.match(/#[0-9A-Fa-f]{6}/g);
          this.primaryColorRaw = match?.[0] || "#FFFFFF";
          this.secondaryColorRaw = match?.[1] || "#FFFFFF";
          this.showGradient = true;
        } else if (config?.background?.color) {
          this.primaryColorRaw = config.background.color;
        }

        if (config?.title) {
          this.titleEnabled = config.title.enabled !== undefined ? config.title.enabled : true;
          this.title = config.title.text || null;
          this.titleColorRaw = config.title.color || "#000000";
          this.titleSize = config.title.size || "md";
          this.titleBold = !!config.title.bold;
          this.titleItalic = !!config.title.italic;
          this.titleUnderline = !!config.title.underline;
          this.titleAlignment = config.title.horizontalAlignment || config.title.align || "center";
        }

        if (config?.body) {
          this.bodyEnabled = config.body.enabled !== undefined ? config.body.enabled : true;
          // Rows whose "v1" body.text already contains real HTML (see
          // looksLikeStructuredHtml() above) were authored directly in the
          // old plain-textarea editor, which always rendered raw HTML via
          // v-html on both the admin preview and the participant view --
          // confirmed (2026-07-20) this is standard, sanctioned authoring
          // behavior, not content that bypassed the editor. Route it through
          // the same raw preload path as genuine V2 content so it loads into
          // Quill unescaped and keeps rendering/editing correctly, instead
          // of being silently destroyed by the legacy escape+wrap path below.
          const hasPreExistingHtml =
            this.contentFormat !== "v2" && looksLikeStructuredHtml(config.body.text);
          if (this.contentFormat === "v2" || hasPreExistingHtml) {
            // V2 (Quill/rich-HTML) configs, and legacy rows with pre-existing
            // real HTML, already store real HTML -- assign directly, with NO
            // decode and NO legacy-bool wrap. Running decodeHtmlBody() here
            // would be actively harmful: contenteditable serialization
            // escapes literal `&` to `&amp;`, so any real `&amp;` in saved
            // HTML would be wrongly un-escaped back to `&` on every reload,
            // corrupting user-authored content.
            this.body = config.body.text || null;
          } else {
            // Genuine V1/legacy plain text -- stored encoded plain text plus
            // separate bold/italic/underline booleans -- decode the
            // entity-encoding, then wrap once in the equivalent inline HTML
            // tags so the new Quill editor displays identical formatting.
            // These booleans are transient (read here, never persisted going
            // forward -- see initialState()'s contentFormat comment).
            const decoded = decodeHtmlBody(config.body.text) || null;
            this.body = wrapLegacyBodyFormatting(decoded, {
              bold: !!config.body.bold,
              italic: !!config.body.italic,
              underline: !!config.body.underline,
            });
          }
          this.bodyColorRaw = config.body.color || "#000000";
          this.bodySize = config.body.size || "md";
          this.bodyAlignment = config.body.horizontalAlignment || config.body.align || "center";
        }

        if (config?.button?.text) {
          this.buttonEnabled = true;
          this.buttonText = config.button.text;
          this.buttonColorRaw = config.button.color || "#09ACEC";
          this.buttonTextColorRaw = config.button.textColor || "#FFFFFF";
        }

        if (config?.externalUrl) {
          this.buttonLink = config.externalUrl;
        }
      } else {
        // New component — apply global defaults. These preload the *display*
        // value only (via the backing field, bypassing the touched-tracking
        // setter) — ADR-0001 Part B requires that a brand-new card's saved
        // config NOT own a value it only ever inherited from globalDefaults.
        if (this.globalDefaults?.cardBackgroundColor) {
          this.primaryColorRaw = this.globalDefaults.cardBackgroundColor;
        }
        if (this.globalDefaults?.textColor) {
          this.titleColorRaw = this.globalDefaults.textColor;
          this.bodyColorRaw = this.globalDefaults.textColor;
        }
        if (this.globalDefaults?.buttonBackgroundColor) {
          this.buttonColorRaw = this.globalDefaults.buttonBackgroundColor;
        }
        if (this.globalDefaults?.buttonTextColor) {
          this.buttonTextColorRaw = this.globalDefaults.buttonTextColor;
        }
      }

      this.$nextTick(() => {
        this.isDirty = false;
      });
    },
    handleBack() {
      if (this.isDirty) {
        this.showUnsavedModal = true;
      } else {
        this.$emit("close");
      }
    },
    handleCancel() {
      if (this.isDirty) {
        this.showUnsavedModal = true;
      } else {
        this.$emit("close");
      }
    },
    handleDiscard() {
      this.showUnsavedModal = false;
      this.$emit("close");
    },
    handleStay() {
      this.showUnsavedModal = false;
    },
    onFileSelected(file) {
      if (this.imageUrl && this.imageUrl.startsWith("blob:")) {
        URL.revokeObjectURL(this.imageUrl);
      }
      if (!file) {
        this.imageFile = null;
        this.imageUrl = null;
        this.imageAspectRatio = false;
        this.markDirty();
        return;
      }
      this.imageFile = file;
      this.imageUrl = URL.createObjectURL(file);
      this.markDirty();
    },
    onClearImage() {
      if (this.imageUrl && this.imageUrl.startsWith("blob:")) {
        URL.revokeObjectURL(this.imageUrl);
      }
      this.imageUrl = null;
      this.imageFile = null;
      this.imageAspectRatio = false;
      this.markDirty();
    },
    onCropRatioApplied(value) {
      this.imageAspectRatio = value;
      this.markDirty();
    },
    // Explicit shape-switch actions (gradient <-> solid color). Both
    // buildConfig()'s shape selection AND onSave()'s post-merge sibling-key
    // force-clear key off `this.showGradient && this.secondaryColor` --
    // toggling `showGradient` alone (with primaryColorTouched/
    // secondaryColorTouched still false, e.g. on a record loaded from an
    // existing gradient/color the user never re-picked this session) makes
    // buildConfig() emit a *null* value for the new shape's own key, which
    // then survives the merge as null AND the force-clear wipes the *old*
    // shape's key too -- the net result is background: {color: null,
    // gradient: null}, i.e. total, silent loss of both possible values (only
    // discovered because the surviving color is expected to just be
    // whatever was already visible in the swatch). Marking the relevant
    // touched flag(s) here makes these explicit user actions assert a
    // concrete value for the shape being switched TO, exactly like directly
    // editing a color swatch would.
    handleRemoveGradient() {
      this.showGradient = false;
      // Collapsing to solid color keeps whatever the primary swatch is
      // currently showing as an explicit, concrete value -- it must not
      // silently fall back to (possibly different) global defaults just
      // because the user never separately touched the primary picker.
      this.primaryColorTouched = true;
    },
    handleAddGradient() {
      this.showGradient = true;
      // Starting a gradient asserts both stops concretely (primary carries
      // over from the existing solid color; secondary starts at its current
      // swatch value, e.g. the "#FFFFFF" default) so an immediate save
      // before picking specific hex values still persists a real gradient
      // instead of nulling both keys.
      this.primaryColorTouched = true;
      this.secondaryColorTouched = true;
    },
    buildConfig() {
      const primaryTouched = this.primaryColorTouched;
      const secondaryTouched = this.secondaryColorTouched;
      const titleColorTouched = this.titleColorTouched;
      const bodyColorTouched = this.bodyColorTouched;
      const buttonColorTouched = this.buttonColorTouched;
      const buttonTextColorTouched = this.buttonTextColorTouched;

      const config = {
        contentFormat: "v2",
        image: {
          enabled: this.imageEnabled,
          url: this.imageUrl,
          aspectRatio: this.imageAspectRatio || false,
        },
        title: {
          enabled: this.titleEnabled,
          text: this.title,
          color: titleColorTouched ? this.titleColor : null,
          bold: this.titleBold,
          italic: this.titleItalic,
          underline: this.titleUnderline,
          size: this.titleSize,
          horizontalAlignment: this.titleAlignment,
        },
        body: {
          enabled: this.bodyEnabled,
          // Bug 8 Quill migration: `this.body` is real HTML from Quill now,
          // saved as-is (no more FormattedBody entity-encoding). bold/
          // italic/underline are hardcoded false -- formatting lives inline
          // in the HTML itself going forward; see contentFormat above,
          // which downstream preload/render logic uses to distinguish this
          // shape from legacy V1 records. `|| ""` preserves the pre-Bug-8
          // guarantee that body.text is never null (previously enforced by
          // FormattedBody()'s `if (!this.body) return ""` guard) -- omitting
          // this turns an empty/never-typed body into `null` instead of `""`,
          // a real behavior change for downstream consumers expecting a string.
          text: this.body || "",
          color: bodyColorTouched ? this.bodyColor : null,
          bold: false,
          italic: false,
          underline: false,
          size: this.bodySize,
          horizontalAlignment: this.bodyAlignment,
        },
      };

      if (this.showGradient && this.secondaryColor) {
        config.background = {
          gradient:
            primaryTouched || secondaryTouched
              ? `linear-gradient(${this.primaryColor}, ${this.secondaryColor})`
              : null,
        };
      } else {
        config.background = { color: primaryTouched ? this.primaryColor : null };
      }

      if (this.buttonEnabled) {
        if (this.buttonText) {
          config.button = {
            text: this.buttonText,
            color: buttonColorTouched ? this.buttonColor : null,
            textColor: buttonTextColorTouched ? this.buttonTextColor : null,
          };
        }
        if (this.buttonLink) {
          config.externalUrl = this.buttonLink;
        }
      }

      return config;
    },
    validateBeforeSave() {
      this.buttonTextError = null;
      this.buttonLinkError = null;

      if (this.buttonEnabled) {
        if (!this.buttonText) {
          this.buttonTextError = "Button Label is required";
        }
        if (!this.buttonLink) {
          this.buttonLinkError = "Button Link is required";
        } else {
          try {
            const parsed = new URL(this.buttonLink);
            if (!["http:", "https:"].includes(parsed.protocol)) {
              this.buttonLinkError = "Button link must be an http or https URL";
            }
          } catch {
            this.buttonLinkError = "Button link is not a valid URL";
          }
        }
      }

      return !this.buttonTextError && !this.buttonLinkError;
    },
    async onSave() {
      if (this.saving) return;
      if (!this.validateBeforeSave()) return;
      this.saving = true;
      this.saveError = null;
      try {
        const config = this.buildConfig();
        // Bug 187345: when a new image was just picked (this.imageFile set),
        // this.imageUrl is a throwaway `blob:` object URL, only valid for this
        // document's lifetime. It must never be persisted as config.image.url
        // -- the uploadDashboardColumnImage() call below (after the column is
        // created/updated) is what establishes the real, permanent URL
        // server-side. Same guard as BillboardCreator.vue's onSave(), adapted
        // to ContentCreator's own image-selection state (imageFile) rather
        // than backgroundMode. Fall back to whatever url was already
        // persisted for this column (empty string for a brand-new column)
        // instead of the ephemeral blob: reference.
        if (this.imageFile) {
          config.image.url = this.column?.config?.image?.url || "";
        }
        let finalConfig = config;
        let columnId =
          this.column?.columnId ?? this.column?.dashboardColumnId ?? this._createdColumnId;
        let isNew = !columnId;

        if (isNew) {
          let rowId = this.row?.rowId || this.rowId;
          if (!rowId) {
            const rowData = {
              rowTypeId: this.row.rowTypeId,
              programId: this.row.programId,
              position: this.rowCount + 1,
            };
            if (this.clientId || this.globalClientId)
              rowData.clientId = this.clientId || this.globalClientId;
            const rowResponse = await ProgramService.createDashboardRow(rowData);
            rowId = rowResponse.data.result.rowId;
            this.rowId = rowId;
          }

          const widgetTypeId = this.widgetTypes?.find((wt) => wt.type === "content")?.widgetTypeId;
          const colResponse = await ProgramService.createDashboardColumn({
            rowId,
            widgetTypeId,
            config,
            position: this.position,
            clientId: this.clientId || this.globalClientId,
          });
          columnId = colResponse.data.result.columnId;
          this._createdColumnId = columnId;
        } else {
          const existingConfig = this.column?.config || {};
          const mergedConfig = nullCoalesceMergeConfig(existingConfig, config);
          // `image` is not touched-ternary-gated (buildConfig() always sends the
          // current concrete imageUrl, including null after onClearImage()), so it
          // must bypass the merge entirely — otherwise clearing an image would be
          // silently reverted to the previously-stored url. See ADR-0001 Part B.
          mergedConfig.image = config.image;
          // `contentFormat` is likewise an unconditional assertion of current
          // save-time shape ("v2", set by buildConfig()) — it must bypass the
          // null-coalesce merge entirely. Legacy V1 records being edited here
          // are always upgraded to V2 on save (Bug 8 Quill migration): once a
          // widget is saved through the new editor, its body is real HTML and
          // must be reloaded as such next time, never re-run through the V1
          // decode/wrap path. Same rationale as `image` above.
          mergedConfig.contentFormat = config.contentFormat;
          // `title.text` / `body.text` are likewise unconditional assertions of
          // current UI state (buildConfig() always sends this.title /
          // this.body, never gated behind a touched flag), so they must
          // also bypass the merge entirely — same rationale as `image` above and
          // as `overlay.text` in BillboardCreator.vue. Not currently reachable via
          // the UI (no `clearable` on the title/body fields), but incidental UI
          // wiring is not a deliberate contract guarantee, so this must not rely
          // on today's field configuration to stay safe. See ADR-0001 Part B.
          mergedConfig.title = { ...mergedConfig.title, text: config.title.text };
          mergedConfig.body = { ...mergedConfig.body, text: config.body.text };
          // `background` is a two-shape object — buildConfig() emits either
          // `{ gradient }` OR `{ color }`, never both keys (not even as null).
          // nullCoalesceMergeConfig spreads all of the *existing* stored
          // object's keys first, then overlays only the keys present on the
          // *new* object — so switching shapes leaves the other shape's key
          // entirely absent from `config`, and it silently survives untouched
          // from the previously-stored config. Force-clear the now-irrelevant
          // sibling key using the same `showGradient && secondaryColor`
          // condition buildConfig() itself uses to choose the shape.
          if (mergedConfig.background) {
            if (this.showGradient && this.secondaryColor) {
              mergedConfig.background.color = null;
            } else {
              mergedConfig.background.gradient = null;
            }
          }
          await ProgramService.updateDashboardColumn(columnId, { config: mergedConfig });
          // The @saved event below must reflect what was actually persisted,
          // not the raw touched-gated buildConfig() output -- otherwise any
          // field the user didn't touch *this session* (button color, title
          // color, etc.) looks nulled-out to whatever's listening (e.g.
          // TripleComponentCreator's slot-card preview) until a full refetch,
          // even though the server has the correct, fully-merged value.
          finalConfig = mergedConfig;
        }

        if (this.imageFile) {
          const uploadRes = await ProgramService.uploadDashboardColumnImage(
            columnId,
            this.imageFile
          );
          // Bug 190085: adopt the permanent URL the upload endpoint returns
          // ({ result: imageUrl }). The 187345 guard above deliberately refuses to persist
          // the throwaway blob: URL and falls back to the previously-stored one -- which is
          // "" for a brand-new column. Without taking the response here, @saved carries that
          // empty url, TripleComponentCreator stores it as the slot's local state, and the
          // `column` watcher below only re-preloads on a columnId change (never on a config
          // change), so the later refresh never reaches an already-open editor. Reopening the
          // slot then renders DragAndDropFileInput's upload prompt instead of the image.
          const uploadedUrl = uploadRes?.data?.result;
          if (!isValidImageUrl(uploadedUrl)) {
            this.saveError = "Upload failed";
            return;
          }
          if (this.imageUrl && this.imageUrl.startsWith("blob:")) {
            URL.revokeObjectURL(this.imageUrl);
          }
          this.imageUrl = uploadedUrl;
          finalConfig = {
            ...finalConfig,
            image: { ...(finalConfig.image || {}), url: uploadedUrl },
          };
          await ProgramService.updateDashboardColumn(columnId, { config: finalConfig });
        }

        const savedRowId = this.row?.rowId || this.rowId;
        this.$emit("saved", { columnId, config: finalConfig, isNew, rowId: savedRowId });
        this.$emit("refresh");
        this.$emit("close");
      } catch (e) {
        this.saveError = e.message || "Save failed";
      } finally {
        this.saving = false;
      }
    },
  },
  watch: {
    title: "markDirty",
    imageEnabled: "markDirty",
    titleEnabled: "markDirty",
    bodyEnabled: "markDirty",
    body: "markDirty",
    primaryColor: "markDirty",
    titleColor: "markDirty",
    titleSize: "markDirty",
    titleBold: "markDirty",
    titleItalic: "markDirty",
    titleUnderline: "markDirty",
    titleAlignment: "markDirty",
    bodyAlignment: "markDirty",
    bodyColor: "markDirty",
    bodySize: "markDirty",
    showGradient() {
      this.markDirty();
    },
    secondaryColor() {
      this.markDirty();
    },
    buttonEnabled() {
      this.markDirty();
      if (!this.buttonEnabled) {
        this.buttonTextError = null;
        this.buttonLinkError = null;
      }
    },
    buttonText() {
      this.markDirty();
      if (this.buttonText) this.buttonTextError = null;
    },
    buttonLink() {
      this.markDirty();
      if (this.buttonLink) this.buttonLinkError = null;
    },
    buttonColor() {
      this.markDirty();
    },
    buttonTextColor() {
      this.markDirty();
    },
    column: {
      handler(newVal, oldVal) {
        const newId = newVal?.columnId ?? newVal?.dashboardColumnId;
        const oldId = oldVal?.columnId ?? oldVal?.dashboardColumnId;
        if (newId !== oldId) {
          this.preloadEditor();
        }
      },
    },
    row: {
      handler(newVal, oldVal) {
        if (newVal?.rowId !== oldVal?.rowId) {
          this.preloadEditor();
        }
      },
    },
  },
};
</script>

<style scoped>
.format-btn--active {
  border: 2px solid currentColor !important;
  border-radius: 4px !important;
}

.color-swatch {
  width: 28px;
  height: 28px;
  flex-shrink: 0;
  cursor: pointer;
  border: 1px solid rgba(0, 0, 0, 0.2);
  border-radius: 0;
  appearance: none;
  -webkit-appearance: none;
  padding: 0;
}
.color-swatch::-webkit-color-swatch-wrapper {
  padding: 0;
}
.color-swatch::-webkit-color-swatch {
  border: none;
}
</style>
